import { skipToken, useApolloClient, useQuery } from '@apollo/client'
import debounce from 'lodash/debounce'
import {
  Building,
  Check,
  ChevronsUpDown,
  CornerDownRight,
  FolderOpen,
  Lock,
  Pencil,
  Plus,
} from 'lucide-react'
import {
  Fragment,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from 'react'
import { useMatch, useSearchParams } from 'react-router'
import { CreateCompanySheet } from '@/components/layout/breadcrumbs/company-switcher/create-company-sheet'
import {
  APP_PROJECT_QUERY,
  DATABASE_PROJECT_QUERY,
  LOAD_BALANCER_PROJECT_QUERY,
  OBJECT_STORAGE_PROJECT_QUERY,
  PIPELINE_PROJECT_QUERY,
  SEARCH_ALL_COMPANIES,
  STATIC_SITE_PROJECT_QUERY,
  SUPERADMIN_QUERY,
} from '@/components/layout/breadcrumbs/company-switcher/gql'
import { UpdateCompanySheet } from '@/components/layout/breadcrumbs/company-switcher/update-company-sheet'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SELECTED_COMPANY_KEY } from '@/config/local-storage'
import {
  useCompanyPath,
  useRBAC,
  useReadQuery,
  useSuspenseQuery,
} from '@/lib/hooks'
import { RoleID } from '@/lib/rbac'
import { cn } from '@/lib/utils'
import type {
  AppProjectSwitcherQuery,
  AppProjectSwitcherQueryVariables,
  DatabaseProjectSwitcherQuery,
  DatabaseProjectSwitcherQueryVariables,
  LoadBalancerProjectSwitcherQuery,
  LoadBalancerProjectSwitcherQueryVariables,
  ObjectStorageProjectSwitcherQuery,
  ObjectStorageProjectSwitcherQueryVariables,
  PipelineProjectSwitcherQuery,
  PipelineProjectSwitcherQueryVariables,
  SearchCompaniesQuery,
  SearchCompaniesQueryVariables,
  StaticSiteProjectSwitcherQuery,
  StaticSiteProjectSwitcherQueryVariables,
  SuperadminCompanySwitcherQuery,
  SuperadminCompanySwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/company-switcher/gql.types.gen'

type CompanyProject = {
  id: string
  name: string
  displayName: string
}

type CompanySwitcherCompany = {
  id: string
  name: string
  projects: {
    id: string
    items: CompanyProject[]
  } | null
}

export const CompanySwitcher = () => {
  const { rbac, rolesInCompany } = useRBAC()
  const isSuperadmin = rbac.kinsta().superadmin.can()
  const canUpdateCompany =
    isSuperadmin ||
    rolesInCompany.some(
      (role) => role === RoleID.COMPANY_OWNER || role === RoleID.COMPANY_ADMIN
    )
  const client = useApolloClient()
  const [open, setOpen] = useState(false)
  const [isUpdateSheetOpen, setIsUpdateSheetOpen] = useState(false)
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false)
  const [value, setValue] = useState('')
  const deferredValue = useDeferredValue(value)
  const [superCompanies, setSuperCompanies] = useState<
    CompanySwitcherCompany[]
  >([])
  const lastSearchRef = useRef('')
  const [searchParams, setSearchParams] = useSearchParams()
  const { navigateToPath } = useCompanyPath()
  const idCompany = searchParams.get('idCompany') ?? ''
  const { userCompaniesQueryRef } = use(QueryRefsContext)
  const { data } = useReadQuery(userCompaniesQueryRef)
  const { data: superadminData } = useSuspenseQuery<
    SuperadminCompanySwitcherQuery,
    SuperadminCompanySwitcherQueryVariables
  >(SUPERADMIN_QUERY, isSuperadmin ? { variables: { idCompany } } : skipToken)

  const sanitizedValue = useMemo(
    () => deferredValue.replace(/^`|`$/g, ''),
    [deferredValue]
  )

  const projectMatch = useMatch('/project/:projectName/*')
  const objectStorageMatch = useMatch('/object-storage/:idObjectStorage/*')
  const appMatch = useMatch('/app/:appName/*')
  const databaseMatch = useMatch('/database/:databaseName/*')
  const pipelineMatch = useMatch('/pipeline/:idPipeline/*')
  const staticSiteMatch = useMatch('/static-site/:staticSiteName/*')
  const loadBalancerMatch = useMatch('/load-balancer/:loadBalancerName/*')

  const projectNameFromPath = useMemo(() => {
    return projectMatch?.params.projectName
  }, [projectMatch])

  const idObjectStorageFromPath = useMemo(() => {
    return objectStorageMatch?.params.idObjectStorage
  }, [objectStorageMatch])

  const appNameFromPath = useMemo(() => {
    return appMatch?.params.appName
  }, [appMatch])

  const databaseNameFromPath = useMemo(() => {
    return databaseMatch?.params.databaseName
  }, [databaseMatch])

  const idPipelineFromPath = useMemo(() => {
    return pipelineMatch?.params.idPipeline
  }, [pipelineMatch])

  const staticSiteNameFromPath = useMemo(() => {
    return staticSiteMatch?.params.staticSiteName
  }, [staticSiteMatch])

  const loadBalancerNameFromPath = useMemo(() => {
    return loadBalancerMatch?.params.loadBalancerName
  }, [loadBalancerMatch])

  const isProjectRoute = Boolean(projectNameFromPath)
  const isObjectStorageRoute = Boolean(idObjectStorageFromPath)
  const isAppRoute = Boolean(appNameFromPath)
  const isDatabaseRoute = Boolean(databaseNameFromPath)
  const isPipelineRoute = Boolean(idPipelineFromPath)
  const isStaticSiteRoute = Boolean(staticSiteNameFromPath)
  const isLoadBalancerRoute = Boolean(loadBalancerNameFromPath)

  const isServiceRoute =
    isProjectRoute ||
    isObjectStorageRoute ||
    isAppRoute ||
    isDatabaseRoute ||
    isPipelineRoute ||
    isStaticSiteRoute ||
    isLoadBalancerRoute

  // Cache-only query to get idProject from object storage (data is already fetched in the object storage page)
  const { data: objectStorageData } = useQuery<
    ObjectStorageProjectSwitcherQuery,
    ObjectStorageProjectSwitcherQueryVariables
  >(OBJECT_STORAGE_PROJECT_QUERY, {
    variables: { idCompany, idObjectStorage: idObjectStorageFromPath ?? '' },
    fetchPolicy: 'cache-only',
    skip: !isObjectStorageRoute || !idObjectStorageFromPath,
  })

  // Cache-only query to get idProject from app
  const { data: appData } = useQuery<
    AppProjectSwitcherQuery,
    AppProjectSwitcherQueryVariables
  >(APP_PROJECT_QUERY, {
    variables: { appName: appNameFromPath ?? '' },
    fetchPolicy: 'cache-only',
    skip: !isAppRoute || !appNameFromPath,
  })

  // Cache-only query to get idProject from database
  const { data: databaseData } = useQuery<
    DatabaseProjectSwitcherQuery,
    DatabaseProjectSwitcherQueryVariables
  >(DATABASE_PROJECT_QUERY, {
    variables: { databaseName: databaseNameFromPath ?? '' },
    fetchPolicy: 'cache-only',
    skip: !isDatabaseRoute || !databaseNameFromPath,
  })

  // Cache-only query to get idProject from pipeline
  const { data: pipelineData } = useQuery<
    PipelineProjectSwitcherQuery,
    PipelineProjectSwitcherQueryVariables
  >(PIPELINE_PROJECT_QUERY, {
    variables: { idCompany, idPipeline: idPipelineFromPath ?? '' },
    fetchPolicy: 'cache-only',
    skip: !isPipelineRoute || !idPipelineFromPath,
  })

  // Cache-only query to get idProject from static site
  const { data: staticSiteData } = useQuery<
    StaticSiteProjectSwitcherQuery,
    StaticSiteProjectSwitcherQueryVariables
  >(STATIC_SITE_PROJECT_QUERY, {
    variables: { staticSiteName: staticSiteNameFromPath ?? '' },
    fetchPolicy: 'cache-only',
    skip: !isStaticSiteRoute || !staticSiteNameFromPath,
  })

  const { data: loadBalancerData } = useQuery<
    LoadBalancerProjectSwitcherQuery,
    LoadBalancerProjectSwitcherQueryVariables
  >(LOAD_BALANCER_PROJECT_QUERY, {
    variables: {
      idCompany,
      loadBalancerName: loadBalancerNameFromPath ?? '',
    },
    fetchPolicy: 'cache-only',
    skip: !isLoadBalancerRoute || !loadBalancerNameFromPath,
  })

  const superadminCompany = useMemo<CompanySwitcherCompany | null>(() => {
    if (isSuperadmin && superadminData?.company) {
      return { ...superadminData.company, projects: null }
    }
    return null
  }, [isSuperadmin, superadminData])

  const companies = useMemo<CompanySwitcherCompany[]>(
    () => data?.user?.companies ?? [],
    [data]
  )
  const filteredCompanies = useMemo(() => {
    if (!sanitizedValue) {
      return companies
    }

    const loweredQuery = sanitizedValue.toLowerCase()
    return companies
      .map((company) => {
        const companyMatches = company.name.toLowerCase().includes(loweredQuery)
        const projects = company.projects?.items ?? []
        const matchingProjects = projects.filter(
          (project) =>
            project.displayName.toLowerCase().includes(loweredQuery) ||
            project.name.toLowerCase().includes(loweredQuery)
        )

        if (!companyMatches && matchingProjects.length === 0) {
          return null
        }

        const projectsToRender = companyMatches ? projects : matchingProjects

        return {
          ...company,
          projects: company.projects
            ? { ...company.projects, items: projectsToRender }
            : null,
        }
      })
      .filter((company): company is CompanySwitcherCompany => company !== null)
  }, [companies, sanitizedValue])
  const activeCompany = useMemo(() => {
    const userCompany =
      (idCompany && companies.find((c) => c.id === idCompany)) || companies[0]
    if (isSuperadmin) {
      const resolvedCompany = superadminCompany || userCompany
      return (
        (resolvedCompany?.id &&
          companies.find((company) => company.id === resolvedCompany.id)) ||
        resolvedCompany
      )
    }
    return userCompany
  }, [idCompany, companies, isSuperadmin, superadminCompany])

  // Get idProject from service routes (cache-only queries)
  const idProjectFromObjectStorage =
    objectStorageData?.company?.objectStorage?.idProject
  const idProjectFromApp = appData?.app?.idProject
  const idProjectFromDatabase = databaseData?.database?.idProject
  const idProjectFromPipeline = pipelineData?.company?.pipeline?.idProject
  const idProjectFromStaticSite = staticSiteData?.staticSite?.idProject
  const idProjectFromLoadBalancer =
    loadBalancerData?.company?.edgeLoadBalancer?.idProject

  const idProjectFromService =
    idProjectFromObjectStorage ||
    idProjectFromApp ||
    idProjectFromDatabase ||
    idProjectFromPipeline ||
    idProjectFromStaticSite ||
    idProjectFromLoadBalancer

  const activeProject = useMemo(() => {
    const projects = activeCompany?.projects?.items ?? []

    // First check if we're on a project route
    if (projectNameFromPath) {
      return projects.find((project) => project.name === projectNameFromPath)
    }

    // Then check if we're on a service route with a linked project
    if (idProjectFromService) {
      return projects.find((project) => project.id === idProjectFromService)
    }

    if (searchParams.get('idProject')) {
      return projects.find(
        (project) => project.id === searchParams.get('idProject')
      )
    }

    return null
  }, [activeCompany, projectNameFromPath, idProjectFromService, searchParams])
  const activeDisplayLabel =
    activeProject?.displayName ?? activeCompany?.name ?? ''
  const isActiveProject = Boolean(activeProject)
  const companiesToRender = useMemo(() => {
    if (isSuperadmin) {
      return value && superCompanies.length ? superCompanies : companies
    }

    return filteredCompanies
  }, [companies, filteredCompanies, isSuperadmin, superCompanies, value])

  const handleOnCreate = useCallback(() => {
    setOpen(false)
    setIsCreateSheetOpen(true)
  }, [setOpen])

  const handleOnCreated = useCallback(
    (idCompany: string) => {
      localStorage.setItem(SELECTED_COMPANY_KEY, idCompany)
      navigateToPath({ pathname: '/', idCompany })
    },
    [navigateToPath]
  )

  const handleOnUpdate = useCallback(() => {
    setOpen(false)
    setIsUpdateSheetOpen(true)
  }, [setOpen])

  const handleOnSelect = useCallback(
    (idCompany: string) => {
      if (idCompany) {
        localStorage.setItem(SELECTED_COMPANY_KEY, idCompany)
        if (isServiceRoute) {
          navigateToPath({ pathname: '/', idCompany })
        } else {
          setSearchParams({ idCompany })
        }
        setOpen(false)
      }
    },
    [isServiceRoute, navigateToPath, setSearchParams, setOpen]
  )

  const handleOnProjectSelect = useCallback(
    (project: CompanyProject, idCompany: string) => {
      if (!project.name) {
        return
      }

      navigateToPath({
        pathname: '/project/:projectName/services',
        variables: { projectName: project.name },
        idCompany,
      })
      setOpen(false)
    },
    [navigateToPath, setOpen]
  )

  const superCompanySearch = useCallback(
    async (query: string) => {
      const { data } = await client.query<
        SearchCompaniesQuery,
        SearchCompaniesQueryVariables
      >({
        query: SEARCH_ALL_COMPANIES,
        variables: { query, limit: 20 },
        fetchPolicy: 'network-only',
      })
      return data.searchCompany
    },
    [client]
  )

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string) => {
        const results = await superCompanySearch(query)
        // Only apply the latest search results
        if (lastSearchRef.current === query) {
          setSuperCompanies(
            results.map((company) => ({ ...company, projects: null }))
          )
        }
      }, 1000),
    [superCompanySearch]
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (!isSuperadmin) {
      return
    }

    if (sanitizedValue.length > 2) {
      lastSearchRef.current = sanitizedValue
      debouncedSearch(sanitizedValue)
    } else {
      lastSearchRef.current = ''
      debouncedSearch.cancel()
      setSuperCompanies([])
    }
  }, [debouncedSearch, isSuperadmin, sanitizedValue])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SidebarMenuButton
          size="lg"
          aria-expanded={open}
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <div className="flex items-center gap-2 text-sm leading-tight">
            <div className="flex size-6 items-center justify-center rounded-md border">
              {isActiveProject ? (
                <FolderOpen className="size-3.5 shrink-0" />
              ) : (
                <Building className="size-3.5 shrink-0" />
              )}
            </div>
            <span
              title={activeDisplayLabel}
              className="max-w-[250px] truncate text-foreground font-medium"
            >
              {activeDisplayLabel}
            </span>
          </div>
          <ChevronsUpDown className="ml-auto" />
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
        align="start"
        side="bottom"
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search for companies..."
            className="h-9"
            value={value}
            onValueChange={(value) => {
              setValue(value)
            }}
          />
          <CommandList>
            <CommandEmpty>No company or project found</CommandEmpty>
            <CommandGroup>
              {companiesToRender.map((company) => {
                const projects = company.projects?.items ?? []

                return (
                  <Fragment key={company.id}>
                    <CommandItem
                      value={company.id}
                      className="cursor-pointer"
                      onSelect={() => handleOnSelect(company.id)}
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activeCompany?.id === company.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Building className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium': activeCompany?.id === company.id,
                        })}
                      >
                        {company.name}
                      </span>
                    </CommandItem>
                    {projects.map((project) => (
                      <CommandItem
                        key={project.id}
                        value={project.id}
                        className="cursor-pointer pl-10"
                        onSelect={() =>
                          handleOnProjectSelect(project, company.id)
                        }
                      >
                        <CornerDownRight
                          className="size-3.5 absolute left-4 top-2.5 opacity-75"
                          aria-hidden={true}
                        />
                        <div className="flex size-6 items-center justify-center rounded-md border">
                          {activeProject?.id === project.id ? (
                            <Check className="size-3.5 shrink-0" />
                          ) : (
                            <FolderOpen className="size-3.5 shrink-0" />
                          )}
                        </div>
                        <span
                          className={cn('truncate', {
                            'font-medium': activeProject?.id === project.id,
                          })}
                        >
                          {project.displayName}
                        </span>
                      </CommandItem>
                    ))}
                  </Fragment>
                )
              })}
            </CommandGroup>
          </CommandList>
          <div className="border-t p-1">
            <button
              type="button"
              disabled={!activeCompany || !canUpdateCompany}
              onClick={handleOnUpdate}
              title={
                canUpdateCompany
                  ? undefined
                  : 'Only company owners or admins can update the company name'
              }
              className="relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-inherit"
            >
              <div className="flex size-6 items-center justify-center rounded-md border">
                {canUpdateCompany ? (
                  <Pencil className="size-3.5" />
                ) : (
                  <Lock className="size-3.5" />
                )}
              </div>
              <span className="truncate">Update company name</span>
            </button>
            <button
              type="button"
              onClick={handleOnCreate}
              className="relative flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground"
            >
              <div className="flex size-6 items-center justify-center rounded-md border">
                <Plus className="size-4" />
              </div>
              <span className="truncate">Create new company</span>
            </button>
          </div>
        </Command>
      </PopoverContent>
      {activeCompany ? (
        <UpdateCompanySheet
          isOpen={isUpdateSheetOpen}
          onClose={() => setIsUpdateSheetOpen(false)}
          idCompany={activeCompany.id}
          initialName={activeCompany.name}
        />
      ) : null}
      <CreateCompanySheet
        isOpen={isCreateSheetOpen}
        onClose={() => setIsCreateSheetOpen(false)}
        onCreated={handleOnCreated}
      />
    </Popover>
  )
}
